import { Loader2, Mail, Send, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface GetInTouchModalProps {
  isOpen: boolean
  onClose: () => void
  /** Pre-fills the email field (e.g. the logged-in user's email). */
  defaultEmail?: string
  /** Pre-fills the full name field (e.g. the logged-in user's name). */
  defaultName?: string
  /** Optional context line shown under the title (e.g. the plan name). */
  subject?: string | undefined
}

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

export default function GetInTouchModal({
  isOpen,
  onClose,
  defaultEmail = '',
  defaultName = '',
  subject,
}: GetInTouchModalProps) {
  const [email, setEmail] = useState(defaultEmail)
  const [fullName, setFullName] = useState(defaultName)
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Re-seed the fields whenever the modal opens or the logged-in user changes,
  // so the email is picked up automatically.
  useEffect(() => {
    if (isOpen) {
      setEmail(defaultEmail)
      setFullName(defaultName)
      setMessage('')
    }
  }, [isOpen, defaultEmail, defaultName])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address')
      return
    }
    if (!message.trim()) {
      toast.error('Please enter a message')
      return
    }

    try {
      setIsSubmitting(true)
      // No contact endpoint is wired yet — acknowledge the enquiry so the user
      // gets feedback. Replace with an API call once the backend supports it.
      await new Promise(resolve => setTimeout(resolve, 600))
      toast.success("Thanks for reaching out — we'll be in touch shortly.")
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm'>
      <div className='bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col'>
        {/* Header */}
        <div className='px-4 sm:px-8 pt-5 sm:pt-7 pb-4'>
          <div className='flex items-start justify-between gap-3'>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center gap-2.5'>
                <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-gray-900 flex-shrink-0'>
                  <Mail className='h-4.5 w-4.5 text-white' />
                </div>
                <h2 className='text-lg sm:text-xl font-bold text-gray-900'>
                  Get in touch
                </h2>
              </div>
              <p className='mt-2 text-xs sm:text-sm text-gray-500'>
                {subject
                  ? `Tell us about your needs for ${subject} and our team will get back to you.`
                  : 'Tell us about your needs and our team will get back to you.'}
              </p>
            </div>
            <button
              type='button'
              onClick={onClose}
              disabled={isSubmitting}
              className='rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-50 flex-shrink-0'
            >
              <X className='h-5 w-5' />
            </button>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className='flex-1 overflow-y-auto px-4 sm:px-8 pb-5'
        >
          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='git-email'
              className='block text-sm font-medium text-gray-700 mb-1.5'
            >
              Email
            </label>
            <input
              id='git-email'
              type='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder='you@example.com'
              className='w-full px-3 py-2.5 border border-gray-300 rounded-lg text-black placeholder:text-gray-400 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900'
              required
            />
          </div>

          <div className='flex flex-col items-start justify-center w-full mt-4'>
            <label
              htmlFor='git-name'
              className='block text-sm font-medium text-gray-700 mb-1.5'
            >
              Full Name
            </label>
            <input
              id='git-name'
              type='text'
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder='Your name'
              className='w-full px-3 py-2.5 border border-gray-300 rounded-lg text-black placeholder:text-gray-400 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900'
            />
          </div>

          <div className='flex flex-col items-start justify-center w-full mt-4'>
            <label
              htmlFor='git-message'
              className='block text-sm font-medium text-gray-700 mb-1.5'
            >
              Message
            </label>
            <textarea
              id='git-message'
              rows={4}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder='Tell us about the number of companies / filings you need…'
              className='w-full px-3 py-2.5 border border-gray-300 rounded-lg text-black placeholder:text-gray-400 outline-none resize-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900'
              required
            />
          </div>

          {/* Footer */}
          <div className='mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3'>
            <button
              type='button'
              onClick={onClose}
              disabled={isSubmitting}
              className='rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isSubmitting}
              className='flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isSubmitting ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Sending...
                </>
              ) : (
                <>
                  <Send className='h-4 w-4' />
                  Send message
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
